import { Link } from "@tanstack/react-router";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { BOOKING_URL, ROOMS_PATH, routeTo } from "@/lib/site";
import { PHOTO_SIZES, PHOTOS } from "../images";

/** Shorter interior hero for the About Us page. */
export function AboutHero() {
  const photo = PHOTOS.cabinRow;

  return (
    <section
      className="relative isolate bg-forest-deep pb-16 sm:pb-20 lg:pb-24"
      aria-labelledby="about-hero-title"
    >
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <img
          src={photo.src}
          srcSet={photo.srcSet}
          sizes={PHOTO_SIZES.fullBleed}
          alt={photo.alt}
          width={photo.width}
          height={photo.height}
          fetchPriority="high"
          decoding="async"
          className="hero-zoom size-full object-cover object-center"
        />
        <div
          className="absolute inset-0 bg-[linear-gradient(180deg,oklch(0.259_0.042_163.6/0.9)_0%,oklch(0.259_0.042_163.6/0.68)_42%,oklch(0.259_0.042_163.6/0.92)_100%)]"
          aria-hidden="true"
        />
      </div>

      <div className="mx-auto max-w-[1400px] px-4 pt-10 sm:px-6 sm:pt-14 lg:px-10 lg:pt-16">
        <div className="hero-stagger max-w-3xl">
          <div style={{ "--stagger": 0 } as React.CSSProperties}>
            <Breadcrumb>
              <BreadcrumbList className="text-cream/75">
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="link-underline text-cream/80 hover:text-cream">
                      Home
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-cream/45 [&>svg]:size-3.5">
                  <ChevronRight />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-medium text-cream">About Us</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <p className="eyebrow mt-8 text-sand" style={{ "--stagger": 1 } as React.CSSProperties}>
            About Amigo Inn &amp; RV Park
          </p>

          <h1
            id="about-hero-title"
            className="mt-4 text-balance font-display text-3xl leading-[1.08] text-cream sm:text-4xl lg:text-[3.35rem]"
            style={{ "--stagger": 2 } as React.CSSProperties}
          >
            A Relaxed South Texas Stay With a Down-Home Feel
          </h1>

          <p
            className="mt-5 max-w-2xl text-base leading-relaxed text-cream/85 sm:text-lg"
            style={{ "--stagger": 3 } as React.CSSProperties}
          >
            Located in Zapata near Falcon Lake, Amigo Inn &amp; RV Park welcomes RV travelers,
            anglers, snowbirds, families, groups, and working guests throughout the year.
          </p>

          <div
            className="mt-8 flex flex-wrap items-center gap-4"
            style={{ "--stagger": 4 } as React.CSSProperties}
          >
            <Button asChild variant="book" size="ctaLg">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book Your Stay <ArrowRight aria-hidden="true" />
              </a>
            </Button>
            <Button asChild variant="outlineLight" size="ctaLg">
              <Link to={routeTo(ROOMS_PATH)}>Explore Sites &amp; Rooms</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
